import { useRef, useState } from 'react';
import StageColumn from './StageColumn.jsx';
import { cn } from '@/lib/utils.js';

// Mobile kanban (spec §3.8): single column visible at a time, swipe
// between stages, sticky stage tabs at top. Drag-to-change-stage is
// NOT supported on mobile — broker uses the ⋯ button on cards
// (always visible at <md:) to open the Move-to menu.

const SWIPE_THRESHOLD = 60; // px

export default function MobilePipelineBoard({ data, stages, sortBy, onContextMenu, onChange }) {
  const [activeIdx, setActiveIdx] = useState(() => {
    // Default to first stage with at least one deal; fallback to first stage.
    const idx = stages.findIndex((s) => (data?.byStage?.[s] || []).length > 0);
    return idx >= 0 ? idx : 0;
  });
  const touchRef = useRef({ startX: 0, startY: 0 });

  if (!data) return null;
  const activeStage = stages[activeIdx];

  function onTouchStart(e) {
    if (!e.touches?.[0]) return;
    touchRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
    };
  }
  function onTouchEnd(e) {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // vertical swipe — ignore
    if (dx < 0 && activeIdx < stages.length - 1) {
      setActiveIdx(activeIdx + 1);
    } else if (dx > 0 && activeIdx > 0) {
      setActiveIdx(activeIdx - 1);
    }
  }

  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-10 -mx-1 flex gap-1 overflow-x-auto border-b border-border bg-bg/95 px-3 py-2 backdrop-blur">
        {stages.map((s, i) => {
          const count = (data.byStage[s] || []).length;
          const active = i === activeIdx;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={cn(
                'shrink-0 rounded-pill px-2.5 py-1 text-[11px] font-medium transition',
                active
                  ? 'bg-accent-soft text-accent'
                  : 'text-text-muted hover:bg-border/30 hover:text-text'
              )}
            >
              {s} <span className="font-mono opacity-70">({count})</span>
            </button>
          );
        })}
      </div>
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="flex flex-col gap-2 p-3"
      >
        <StageColumn
          key={activeStage}
          stage={activeStage}
          items={data.byStage[activeStage] || []}
          sortBy={sortBy}
          onContextMenu={onContextMenu}
          onChange={onChange}
        />
      </div>
    </div>
  );
}
